// src/components/CompanyManagementSidebar.tsx
import React, { useState, useEffect, useCallback } from "react";
import { FiX, FiEdit3, FiFolder, FiMove, FiTrash2, FiSave } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { confirm } from "../../lib/dialogs";
import { 
  updateCompany, 
  deleteCompany,
  listCompanies
} from "../../services/companies";
import { 
  listProjects,
  updateProject
} from "../../services/projects";
import { useUserRole } from "../../hooks/useUserRole";
import type { Company, Project } from "../../types/domain";
import "../CompanyManagementSidebar.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: Company | null;
  onCompanyUpdated: () => void;
}

export default function CompanyManagementSidebar({ 
  isOpen, 
  onClose, 
  selectedCompany, 
  onCompanyUpdated 
}: Props) {
  const [activeTab, setActiveTab] = useState<'edit' | 'projects'>('edit');
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Projects management
  const [projects, setProjects] = useState<Project[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [targetCompanyId, setTargetCompanyId] = useState<string>("");
  
  
  const { isSuperAdmin } = useUserRole();

  const loadData = useCallback(async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      // Load projects
      const { data: projectsData } = await listProjects(selectedCompany.id);
      if (projectsData) setProjects(projectsData);
      
      // Load all companies for moving projects
      const { data: companiesData } = await listCompanies();
      if (companiesData) setAllCompanies(companiesData.filter(c => c.id !== selectedCompany.id));
      
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  }, [selectedCompany]);

  useEffect(() => {
    if (isOpen && selectedCompany) {
      setEditName(selectedCompany.name);
      loadData();
    }
  }, [isOpen, selectedCompany, loadData]);


  const handleUpdateCompany = async () => {
    if (!selectedCompany || !editName.trim()) return;
    
    const confirmed = await confirm({
      title: "Actualizar empresa",
      text: `¿Actualizar el nombre de la empresa a "${editName.trim()}"?`,
      icon: "question",
      confirmText: "Actualizar",
    });
    
    if (!confirmed) return;
    
    try {
      const { error } = await updateCompany(selectedCompany.id, { name: editName.trim() });
      if (error) throw error;
      
      toast.success("Empresa actualizada");
      onCompanyUpdated();
    } catch (error) {
      toast.error("Error actualizando empresa");
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    
    const confirmed = await confirm({
      title: "Eliminar empresa",
      text: `¿Estás seguro de eliminar la empresa "${selectedCompany.name}"? Esta acción eliminará todos sus proyectos y tareas.`,
      icon: "warning",
      confirmText: "Eliminar",
    });
    
    if (!confirmed) return;
    
    try {
      const { error } = await deleteCompany(selectedCompany.id);
      if (error) throw error;
      
      toast.success("Empresa eliminada");
      onCompanyUpdated();
      onClose();
    } catch (error) {
      toast.error("Error eliminando empresa");
    }
  };

  const handleMoveProjects = async () => {
    if (!selectedProjects.length || !targetCompanyId) {
      toast.error("Selecciona proyectos y empresa destino");
      return;
    }
    
    const targetCompany = allCompanies.find(c => c.id === targetCompanyId);
    const confirmed = await confirm({
      title: "Mover proyectos",
      text: `¿Mover ${selectedProjects.length} proyecto(s) a "${targetCompany?.name}"?`,
      icon: "question",
      confirmText: "Mover",
    });
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      await Promise.all(
        selectedProjects.map(projectId =>
          updateProject(projectId, { company_id: targetCompanyId })
        )
      );
      
      toast.success("Proyectos movidos exitosamente");
      setSelectedProjects([]);
      setTargetCompanyId("");
      loadData();
      onCompanyUpdated();
    } catch (error) {
      toast.error("Error moviendo proyectos");
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen || !selectedCompany) return null;

  return (
    <div className="company-sidebar-overlay">
      <div className="company-sidebar">
        <div className="company-sidebar-header">
          <h3>Gestionar: {selectedCompany.name}</h3>
          <button className="close-btn" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>
        
        <div className="company-sidebar-tabs">
          <button 
            className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            <FiEdit3 size={16} />
            Editar
          </button>
          <button 
            className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            <FiFolder size={16} />
            Proyectos
          </button>
        </div>

        <div className="company-sidebar-content">
          {activeTab === 'edit' && (
            <div className="tab-content">
              <h4>Información de la empresa</h4>
              <div className="form-group">
                <label>Nombre de la empresa</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre de la empresa"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  className="btn primary"
                  onClick={handleUpdateCompany}
                  disabled={!editName.trim() || editName === selectedCompany.name}
                >
                  <FiSave size={16} />
                  Actualizar
                </button>
                
                {isSuperAdmin() && (
                  <button 
                    className="btn danger"
                    onClick={handleDeleteCompany}
                  >
                    <FiTrash2 size={16} />
                    Eliminar empresa
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="tab-content">
              <h4>Gestionar proyectos ({projects.length})</h4>
              
              <div className="move-projects-section">
                <h5>Mover proyectos a otra empresa</h5>
                
                <div className="form-group">
                  <label>Empresa destino</label>
                  <select
                    value={targetCompanyId}
                    onChange={(e) => setTargetCompanyId(e.target.value)}
                  >
                    <option value="">Seleccionar empresa...</option>
                    {allCompanies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="projects-list">
                  {projects.map(project => (
                    <div key={project.id} className="project-item">
                      <label className="project-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProjects(prev => [...prev, project.id]);
                            } else {
                              setSelectedProjects(prev => prev.filter(id => id !== project.id));
                            }
                          }}
                        />
                        <span>{project.name}</span>
                      </label>
                    </div>
                  ))}
                </div>

                {selectedProjects.length > 0 && targetCompanyId && (
                  <button 
                    className="btn primary"
                    onClick={handleMoveProjects}
                    disabled={loading}
                  >
                    <FiMove size={16} />
                    Mover {selectedProjects.length} proyecto(s)
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}